import { useState, useEffect, useRef } from "react";
import {
  contributorSubmissionFailureCode,
  testsApi,
} from "@/api/tests.api";
import { getMediaRecorderOptions, getAudioExtension } from "@/config/audio.config";
import { normalizeLanguageKey } from "../helpers";
import { TEST_TIME_LIMIT, TRANSLATION_TIME_LIMIT } from "../constants";
import type { TestSummary } from "@/lib/types/tests";
import type { AppContextValue } from "@/contexts/app-context.shared";
import type { GetPaidToast } from "./get-paid-context";

interface SubmissionInfo {
  status: 'PENDING' | 'VALIDATED' | 'REJECTED';
  submittedAt?: string;
  approvedAt?: string;
  acceptedBaseline?: number;
  totalScore?: number | null;
}

export function useTestTaking(
  t: AppContextValue["t"],
  toast: GetPaidToast,
  currentLang: string,
  mySubmissions: Record<string, SubmissionInfo>,
  setMySubmissions: React.Dispatch<React.SetStateAction<Record<string, SubmissionInfo>>>,
) {
  const [currentTest, setCurrentTest] = useState<TestSummary | null>(null);
  const [selectedTestLang, setSelectedTestLang] = useState(currentLang);

  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [timers, setTimers] = useState<Record<string, number>>({});
  const [statuses, setStatuses] = useState<Record<string, "idle" | "in_progress" | "completed" | "expired">>({});
  const [attempts, setAttempts] = useState<Record<string, number>>({});
  const [audioUrls, setAudioUrls] = useState<Record<string, string | null>>({});
  const [audioBlobs, setAudioBlobs] = useState<Record<string, Blob | null>>({});
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [testTimeRemaining, setTestTimeRemaining] = useState(TEST_TIME_LIMIT);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const countdownRef = useRef<number | null>(null);
  const testCountdownRef = useRef<number | null>(null);
  const submissionKeyRef = useRef<string | null>(null);
  const submittingRef = useRef(false);

  const [dataPhonemes, setDataPhonemes] = useState<string[]>([]);
  const [dataPhrasesLocalFr, setDataPhrasesLocalFr] = useState<{ local: string; french?: string }[]>([]);
  const [dataPhrasesLocalOnly, setDataPhrasesLocalOnly] = useState<{ local: string }[]>([]);
  const [startingTest, setStartingTest] = useState(false);
  const [submittingUserTest, setSubmittingUserTest] = useState(false);

  const isTestExpired = !!currentTest && testTimeRemaining === 0;

  const stopRecordingInternal = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try {
        recorderRef.current.stop();
      } catch {
        // The recorder may already have stopped before cleanup runs.
      }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (countdownRef.current) {
      window.clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setActiveItemId(null);
  };

  const startCountdown = (itemId: string, seconds: number, onExpire: () => void) => {
    setTimers((prev) => ({ ...prev, [itemId]: seconds }));
    if (countdownRef.current) window.clearInterval(countdownRef.current);
    countdownRef.current = window.setInterval(() => {
      setTimers((prev) => {
        const next = Math.max(0, (prev[itemId] ?? seconds) - 1);
        const updated = { ...prev, [itemId]: next };
        if (next === 0) {
          window.clearInterval(countdownRef.current!);
          countdownRef.current = null;
          onExpire();
        }
        return updated;
      });
    }, 1000);
  };

  const startRecording = async (itemId: string, maxSeconds: number) => {
    if (isTestExpired) {
      toast({ title: t.getPaid.testTimeElapsedTitle, description: t.getPaid.testFinished, variant: "destructive" });
      return;
    }
    const used = attempts[itemId] ?? 0;
    if (used >= 2) return;

    stopRecordingInternal();
    setActiveItemId(itemId);
    setStatuses((s) => ({ ...s, [itemId]: "in_progress" }));
    setAttempts((a) => ({ ...a, [itemId]: used + 1 }));

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const chunks: BlobPart[] = [];
      const rec = new MediaRecorder(stream, getMediaRecorderOptions());
      recorderRef.current = rec;
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/wav" });
        const url = URL.createObjectURL(blob);
        submissionKeyRef.current = null;
        setAudioUrls((prev) => ({ ...prev, [itemId]: url }));
        setAudioBlobs((prev) => ({ ...prev, [itemId]: blob }));
        setStatuses((s) => ({ ...s, [itemId]: "completed" }));
      };
      rec.start();
      startCountdown(itemId, maxSeconds, () => {
        if (rec.state !== "inactive") rec.stop();
        stopRecordingInternal();
        toast({ title: t.getPaid.testTimeElapsedTitle, description: t.getPaid.recordingStoppedAuto });
      });
    } catch (e) {
      setStatuses((s) => ({ ...s, [itemId]: "idle" }));
      setAttempts((a) => ({ ...a, [itemId]: used }));
      toast({ title: t.getPaid.microphoneUnavailable, description: t.getPaid.microphonePermissionRequired, variant: "destructive" });
      stopRecordingInternal();
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    stopRecordingInternal();
  };

  const redoRecording = (itemId: string, maxSeconds: number) => {
    const used = attempts[itemId] ?? 0;
    if (used >= 2) return;
    submissionKeyRef.current = null;
    setAudioUrls((prev) => ({ ...prev, [itemId]: null }));
    setAudioBlobs((prev) => ({ ...prev, [itemId]: null }));
    startRecording(itemId, maxSeconds);
  };

  const handleTranslationFocus = (itemId: string) => {
    if (isTestExpired) return;
    if (timers[itemId] !== undefined) return;

    setStatuses((s) => ({ ...s, [itemId]: "in_progress" }));
    startCountdown(itemId, TRANSLATION_TIME_LIMIT, () => {
      setStatuses((s) => ({ ...s, [itemId]: translations[itemId]?.trim() ? "completed" : "expired" }));
      toast({ title: t.getPaid.testTimeElapsedTitle, description: t.getPaid.inputCompleted });
    });
  };

  const handleTranslationChange = (itemId: string, value: string) => {
    if (isTestExpired) return;
    submissionKeyRef.current = null;
    setTranslations((prev) => ({ ...prev, [itemId]: value }));
    if (value.trim()) setStatuses((s) => ({ ...s, [itemId]: "completed" }));
  };

  const allItemsCompleted = () => {
    const phonemeIds = dataPhonemes.map((_, i) => `ph-${i}`);
    const pronounceIds = dataPhrasesLocalFr.map((_, i) => `pp-${i}`);
    const translateIds = dataPhrasesLocalOnly.map((_, i) => `tp-${i}`);
    const required = [...phonemeIds, ...pronounceIds, ...translateIds];
    return required.every((id) => statuses[id] === "completed");
  };

  const handleFinalSubmit = async () => {
    if (!currentTest || submittingRef.current) return;
    if (!allItemsCompleted()) {
      toast({ title: t.getPaid.incomplete, description: t.getPaid.completeAllItems, variant: "destructive" });
      return;
    }

    submittingRef.current = true;
    setSubmittingUserTest(true);

    const phrasesPayload = [
      ...dataPhrasesLocalFr.map((p) => ({ local: p.local, french: p.french })),
      ...dataPhrasesLocalOnly.map((p, i) => ({ local: p.local, userAnswer: translations[`tp-${i}`] || "" })),
    ];

    try {
      const basePayload = {
        language: currentTest.language,
        answers: { phonemes: dataPhonemes, phrases: phrasesPayload },
      };
      const idempotencyKey = submissionKeyRef.current ?? crypto.randomUUID();
      submissionKeyRef.current = idempotencyKey;
      const hasAudio = Object.values(audioBlobs).some((b) => !!b);
      if (hasAudio) {
        const form = new FormData();
        form.append("language", basePayload.language);
        form.append("answers", JSON.stringify(basePayload.answers));
        Object.entries(audioBlobs).forEach(([id, blob]) => {
          if (blob) form.append("audioFiles", blob, `${id}.${getAudioExtension(blob.type)}`);
        });
        await testsApi.submitContributorTestWithAudio(form, idempotencyKey);
      } else {
        await testsApi.submitContributorTest(basePayload, idempotencyKey);
      }
      toast({ title: t.getPaid.testSubmitted, description: t.getPaid.testSubmittedDesc });
      setMySubmissions((prev) => ({
        ...prev,
        [currentTest.language]: { status: 'PENDING', submittedAt: new Date().toISOString(), totalScore: null },
      }));
      submissionKeyRef.current = null;
      setCurrentTest(null);
      setAudioUrls({});
      setAudioBlobs({});
      setTranslations({});
      setStatuses({});
      setAttempts({});
      setTimers({});
      setDataPhonemes([]);
      setDataPhrasesLocalFr([]);
      setDataPhrasesLocalOnly([]);
    } catch (error) {
      const failureCode = contributorSubmissionFailureCode(error);
      const description = failureCode === "SUBMISSION_IN_PROGRESS"
        ? t.getPaid.testSubmissionInProgress
        : failureCode === "IDEMPOTENCY_KEY_REUSED"
          ? t.getPaid.testSubmissionChanged
          : t.getPaid.submitTestFailed;
      toast({ title: t.error, description, variant: "destructive" });
    } finally {
      submittingRef.current = false;
      setSubmittingUserTest(false);
    }
  };

  const handleStartTest = async () => {
    try {
      setStartingTest(true);
      if (!selectedTestLang) {
        toast({ title: t.getPaidPage.languageRequiredTitle, description: t.getPaidPage.languageRequiredDesc, variant: "destructive" });
        return;
      }
      const st = mySubmissions[normalizeLanguageKey(selectedTestLang)];
      if (st && (st.status === 'PENDING' || st.status === 'VALIDATED')) {
        const msg = st.status === 'PENDING' ? t.getPaid.pendingTestExists : t.getPaid.validatedTestExists;
        toast({ title: t.getPaid.testAlreadySubmitted, description: msg, variant: "destructive" });
        return;
      }
      const data = await testsApi.getRandomDefinitionsByLang(selectedTestLang);
      setDataPhonemes(Array.isArray(data?.phonemes) ? data.phonemes : []);
      setDataPhrasesLocalFr(Array.isArray(data?.phrasesLocalFr) ? data.phrasesLocalFr : []);
      setDataPhrasesLocalOnly(Array.isArray(data?.phrasesLocalOnly) ? data.phrasesLocalOnly : []);

      const mockTest: TestSummary = {
        id: "test-" + Date.now(),
        title: t.getPaid.qualificationTest,
        summary: t.transcribeAudioIntoFrench,
        rewardPoints: 50,
        status: "open",
        language: selectedTestLang,
        questions: 1,
        timeLimit: TEST_TIME_LIMIT,
        createdAt: new Date().toISOString(),
      };
      submissionKeyRef.current = crypto.randomUUID();
      setCurrentTest(mockTest);
    } catch (error) {
      toast({ title: t.error, description: t.getPaid.loadTestFailed, variant: "destructive" });
    } finally {
      setStartingTest(false);
    }
  };

  useEffect(() => {
    return () => {
      if (testCountdownRef.current) {
        window.clearInterval(testCountdownRef.current);
        testCountdownRef.current = null;
      }
      stopRecordingInternal();
    };
  }, []);

  useEffect(() => {
    if (testCountdownRef.current) {
      window.clearInterval(testCountdownRef.current);
      testCountdownRef.current = null;
    }

    if (!currentTest) {
      setTestTimeRemaining(TEST_TIME_LIMIT);
      return;
    }

    setTestTimeRemaining(currentTest.timeLimit || TEST_TIME_LIMIT);
    testCountdownRef.current = window.setInterval(() => {
      setTestTimeRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => {
      if (testCountdownRef.current) {
        window.clearInterval(testCountdownRef.current);
        testCountdownRef.current = null;
      }
    };
  }, [currentTest]);

  useEffect(() => {
    if (!currentTest || testTimeRemaining > 0) return;

    stopRecordingInternal();
    toast({
      title: t.getPaid.testTimeElapsedTitle,
      description: t.getPaid.testTimeElapsedDesc,
      variant: "destructive",
    });
  }, [currentTest, testTimeRemaining, toast, t.getPaid.testTimeElapsedDesc, t.getPaid.testTimeElapsedTitle]);

  return {
    currentTest,
    setCurrentTest,
    selectedTestLang,
    setSelectedTestLang,
    dataPhonemes,
    dataPhrasesLocalFr,
    dataPhrasesLocalOnly,
    testTimeRemaining,
    isTestExpired,
    submittingUserTest,
    startingTest,
    activeItemId,
    timers,
    statuses,
    attempts,
    audioUrls,
    translations,
    startRecording,
    stopRecording,
    redoRecording,
    handleTranslationFocus,
    handleTranslationChange,
    handleFinalSubmit,
    handleStartTest,
    allItemsCompleted,
  };
}
